import { cn } from "@/lib/utils";
import type { ChildProps } from "@/types/ui";
import type { HTMLAttributes, ReactNode } from "react";
import { forwardRef, isValidElement } from "react";

const cardVariants = {
  default: "rounded-lg border bg-card text-card-foreground shadow",
  glass: "rounded-xl border border-border bg-card text-card-foreground shadow-card",
  elevated: "rounded-xl bg-card text-card-foreground shadow-elevated",
  gradient: "rounded-xl bg-card text-card-foreground shadow-card",
};

interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "variant"> {
  variant?: keyof typeof cardVariants;
  asChild?: boolean;
}

const Card = forwardRef<HTMLDivElement, CardProps>(({ className, children, variant = "default", asChild = false, ...props }, ref) => {
  const baseClass = cardVariants[variant] ?? cardVariants.default;
  const cardClass = cn(baseClass, "transition-all duration-200", className);

  if (asChild && isValidElement(children)) {
    const child = children as React.ReactElement & ChildProps;
    const childProps = child.props as ChildProps["props"];
    const childClassName = childProps.className as string | undefined;
    const childKey = child.key;
    const { className: _c, key: _k, children: _ch, ...restProps } = childProps;
    return (
      <div ref={ref} key={childKey} className={cn(childClassName, cardClass)} {...restProps}>
        {childProps.children as ReactNode}
      </div>
    );
  }

  return (
    <div className={cardClass} ref={ref} {...props}>
      {children}
    </div>
  );
});
Card.displayName = "Card";

const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div className={cn("flex flex-col space-y-1.5 p-4 sm:p-6 transition-opacity duration-200", className)} ref={ref} {...props} />
));
CardHeader.displayName = "CardHeader";

const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(({ className, ...props }, ref) => (
  <h3 className={cn("font-semibold leading-none tracking-tight transition-colors duration-200", className)} ref={ref} {...props} />
));
CardTitle.displayName = "CardTitle";

const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div className={cn("p-4 sm:p-6 pt-0 transition-all duration-200", className)} ref={ref} {...props} />
));
CardContent.displayName = "CardContent";

export { Card, CardHeader, CardTitle, CardContent };
